import { useState } from 'react';
import { Button, Input, Select, Checkbox, Badge, Spinner } from '../atoms';

/**
 * Atoms demo page at /v2/__/atoms.
 *
 * Visual catalog of every atom variant + size + state. Used for design review +
 * manual regression testing. Phase 11 Block 6 PR 2 deliverable.
 *
 * Will graduate to /v2/__/components when more component tiers ship.
 */

export function AtomsDemo() {
  const [checked, setChecked] = useState(true);
  const [selectVal, setSelectVal] = useState('active');
  const [inputVal, setInputVal] = useState('Sample text');

  return (
    <div style={{
      maxWidth: 960,
      margin: 'var(--sp-10) auto',
      padding: `0 var(--sp-5)`,
    }}>
      <header style={{
        borderBottom: `1px solid var(--color-border)`,
        paddingBottom: 'var(--sp-5)',
        marginBottom: 'var(--sp-7)',
      }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
        }}>Atoms</h1>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
        }}>
          Phase 11 Block 6 PR 2. Button · Input · Select · Checkbox · Badge · Spinner.
        </p>
      </header>

      <Section title="Button — variants × sizes">
        <Row>
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="danger">Danger</Button>
        </Row>
        <Row>
          <Button size="sm">Small</Button>
          <Button size="md">Medium</Button>
          <Button size="lg">Large</Button>
          <Button disabled>Disabled</Button>
          <Button loading>Loading</Button>
        </Row>
      </Section>

      <Section title="Input">
        <Input
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          placeholder="Type something…"
        />
        <Input invalid placeholder="Invalid state (aria-invalid)" defaultValue="bad" />
        <Input disabled placeholder="Disabled" defaultValue="readonly-ish" />
      </Section>

      <Section title="Select">
        <Select
          value={selectVal}
          onChange={(e) => setSelectVal(e.target.value)}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'in_setup', label: 'In Setup' },
            { value: 'historical', label: 'Historical' },
          ]}
        />
      </Section>

      <Section title="Checkbox">
        <Checkbox
          label="Send notification on status change"
          description="The customer's primary contact is emailed when the appeal flips status."
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
        />
        <Checkbox label="Disabled option" disabled />
      </Section>

      <Section title="Badge — semantic variants">
        <Row>
          <Badge variant="success" dot>Active</Badge>
          <Badge variant="warning" dot>Pending</Badge>
          <Badge variant="danger" dot>Failed</Badge>
          <Badge variant="info" dot>Settled</Badge>
          <Badge variant="neutral" dot>Draft</Badge>
        </Row>
        <Row>
          <Badge variant="success">No Dot</Badge>
          <Badge variant="warning">Compact</Badge>
        </Row>
      </Section>

      <Section title="Spinner">
        <Row>
          <Spinner size="sm" />
          <Spinner size="md" />
          <Spinner size="lg" />
        </Row>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{
      background: 'var(--color-card)',
      border: `1px solid var(--color-border)`,
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-5)',
      marginBottom: 'var(--sp-5)',
      boxShadow: 'var(--sh-sm)',
    }}>
      <h2 style={{
        margin: '0 0 var(--sp-4)',
        fontSize: 'var(--fs-xl)',
        fontWeight: 'var(--fw-semibold)',
        color: 'var(--color-ink)',
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        {children}
      </div>
    </section>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
      {children}
    </div>
  );
}
